import React, { createContext, useContext, useState } from 'react';
import { UnpostedKid } from 'models/UnpostedKid';
import { Kid } from 'models/Kid';
import client from 'shared/ApiClient';
import { consoleLogInDev } from 'shared';
import { useJobFormContext } from '../JobFormContext';

interface KidPetContextType {
    unpostedKids: UnpostedKid[];
    setUnpostedKids: (e: UnpostedKid[]) => void;
    postFamilyKids: (familyId: number, ongoingId?: number) => Promise<void>;
    validateKidPetInfo: () => boolean;
}

interface KidPetContextProps {
    children?: React.ReactNode;
}

export const KidPetContext = createContext<KidPetContextType>(undefined as any);

export function KidPetContextProvider({ children }: KidPetContextProps) {
    const { form: jobForm } = useJobFormContext();

    const initialValue = [
        { id: 0, name: '', birthday: '', type: '', comments: '', isName: false, isBirthday: false, isType: true },
    ];
    const [unpostedKids, setUnpostedKids] = useState<UnpostedKid[]>(initialValue);

    function resetUnpostedKids() {
        setUnpostedKids(initialValue);
    }

    async function postFamilyKids(familyId: number) {
        let kids = unpostedKids
            .filter((kid) => !isInvalidDependent(kid))
            .map((kid) => ({
                name: kid.name,
                birthday: kid.birthday ? kid.birthday + '-01' : null,
                comments: kid.comments,
                family: familyId,
                present: true,
                type: kid.type.toUpperCase(),
            }));

        if (kids.length === 0) return;

        try {
            const postedKids = await client('api/kids/', {
                body: { dependents_array: kids },
            });

            jobForm.fieldStates.kids.value = postedKids;
            jobForm.fieldStates.kids.setValue(postedKids);
            resetUnpostedKids();
        } catch (error) {
            consoleLogInDev(error);
        }
    }

    function isInvalidDependent(dependent: UnpostedKid) {
        const invalid =
            (dependent.type === 'CHILD' && dependent.isBirthday === false) ||
            dependent.isName === false ||
            dependent.isType === false;
        return invalid;
    }

    function validateKidPetInfo() {
        const anyInfoInvalid = unpostedKids.find(isInvalidDependent);

        return !anyInfoInvalid;
    }

    const contextValue = {
        postFamilyKids,
        unpostedKids,
        setUnpostedKids,
        validateKidPetInfo,
    };

    return <KidPetContext.Provider value={contextValue}>{children}</KidPetContext.Provider>;
}

export function useKidPetContext() {
    return useContext(KidPetContext);
}
